import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { RoleGuard } from '@/auth/RoleGuard';
import { AppShell } from '@/components/layout/AppShell';
import { DashboardPage } from '@/pages/DashboardPage';
import { EquipmentDetailPage } from '@/pages/equipment/EquipmentDetailPage';
import { EquipmentFormPage } from '@/pages/equipment/EquipmentFormPage';
import { EquipmentsListPage } from '@/pages/equipment/EquipmentsListPage';
import { VesselEquipmentTreePage } from '@/pages/equipment/VesselEquipmentTreePage';
import { WorkOrderDetailPage } from '@/pages/work-orders/WorkOrderDetailPage';
import { WorkOrderFormPage } from '@/pages/work-orders/WorkOrderFormPage';
import { WorkOrdersListPage } from '@/pages/work-orders/WorkOrdersListPage';
import { MaintenancePlanDetailPage } from '@/pages/maintenance-plans/MaintenancePlanDetailPage';
import { MaintenancePlanFormPage } from '@/pages/maintenance-plans/MaintenancePlanFormPage';
import { MaintenancePlansListPage } from '@/pages/maintenance-plans/MaintenancePlansListPage';
import { PartDetailPage } from '@/pages/parts/PartDetailPage';
import { PartFormPage } from '@/pages/parts/PartFormPage';
import { PartsListPage } from '@/pages/parts/PartsListPage';
import { StockLotDetailPage } from '@/pages/stock-lots/StockLotDetailPage';
import { StockLotFormPage } from '@/pages/stock-lots/StockLotFormPage';
import { StockLotsListPage } from '@/pages/stock-lots/StockLotsListPage';
import { CertificateDetailPage } from '@/pages/certificates/CertificateDetailPage';
import { CertificateFormPage } from '@/pages/certificates/CertificateFormPage';
import { CertificatesListPage } from '@/pages/certificates/CertificatesListPage';
import { DrydockCampaignDetailPage } from '@/pages/drydock-campaigns/DrydockCampaignDetailPage';
import { DrydockCampaignFormPage } from '@/pages/drydock-campaigns/DrydockCampaignFormPage';
import { DrydockCampaignsListPage } from '@/pages/drydock-campaigns/DrydockCampaignsListPage';
import { ForbiddenPage } from '@/pages/ForbiddenPage';
import { LoginPage } from '@/pages/LoginPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { VesselDetailPage } from '@/pages/vessels/VesselDetailPage';
import { VesselFormPage } from '@/pages/vessels/VesselFormPage';
import { VesselsListPage } from '@/pages/vessels/VesselsListPage';

/**
 * Lazy-loaded route helper. React Router's `lazy` field expects a
 * module exposing `Component` (or `element`); we wrap a regular
 * dynamic import that re-keys a named page export under that field.
 *
 * Lazy targets are pages the average operator hits rarely:
 *   - admin gate (users / companies / equipment-types / part-types)
 *   - QR scan + offline queue (heavy code paths)
 *   - personal + company settings
 *
 * Hot-path routes (vessels / equipment / work-orders / parts /
 * maintenance / certificates / drydock / dashboard) stay eagerly
 * imported so the first authenticated render doesn't pay a network
 * round-trip.
 */
function lazyComponent<P extends string>(
  loader: () => Promise<Record<P, React.ComponentType>>,
  exportName: P,
) {
  return async () => {
    const mod = await loader();
    return { Component: mod[exportName] };
  };
}

/**
 * App router.
 *
 * Public:
 *   /login                → LoginPage
 *   /403                  → ForbiddenPage (rendered by RoleGuard misses)
 *
 * Protected (ProtectedRoute → /me, then AppShell layout with sidebar
 * + topbar wraps every authenticated page):
 *   /                     → redirect to /dashboard
 *   /dashboard            → DashboardPage
 *
 * 404:
 *   *                     → NotFoundPage
 */
export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/403', element: <ForbiddenPage /> },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/', element: <Navigate to="/dashboard" replace /> },
          { path: '/dashboard', element: <DashboardPage /> },
          {
            path: '/scan',
            lazy: lazyComponent(
              () => import('@/pages/scan/ScanPage'),
              'ScanPage',
            ),
          },
          {
            path: '/offline-queue',
            lazy: lazyComponent(
              () => import('@/pages/offline-queue/OfflineQueuePage'),
              'OfflineQueuePage',
            ),
          },
          {
            path: '/settings',
            lazy: lazyComponent(
              () => import('@/pages/settings/SettingsPage'),
              'SettingsPage',
            ),
          },
          {
            // L12 — Security settings (TOTP enrollment / disable).
            // All authenticated users — admins are forced via the
            // policy bit, others can opt-in.
            path: '/settings/security',
            lazy: lazyComponent(
              () => import('@/pages/security/SecuritySettingsPage'),
              'SecuritySettingsPage',
            ),
          },
          { path: '/vessels', element: <VesselsListPage /> },
          { path: '/vessels/new', element: <VesselFormPage /> },
          { path: '/vessels/:id', element: <VesselDetailPage /> },
          { path: '/vessels/:id/edit', element: <VesselFormPage /> },
          { path: '/vessels/:id/equipment', element: <VesselEquipmentTreePage /> },
          {
            path: '/vessels/:id/liquids',
            lazy: lazyComponent(
              () => import('@/pages/liquids/VesselLiquidsPage'),
              'VesselLiquidsPage',
            ),
          },
          {
            // L14 — vessel-scoped inspection rounds (start + history).
            path: '/vessels/:id/inspection-rounds',
            lazy: lazyComponent(
              () => import('@/pages/inspection-rounds/VesselInspectionsPage'),
              'VesselInspectionsPage',
            ),
          },
          {
            path: '/inspection-rounds/:executionId',
            lazy: lazyComponent(
              () => import('@/pages/inspection-rounds/InspectionExecutionPage'),
              'InspectionExecutionPage',
            ),
          },
          { path: '/equipment', element: <EquipmentsListPage /> },
          { path: '/equipment/new', element: <EquipmentFormPage /> },
          { path: '/equipment/:id', element: <EquipmentDetailPage /> },
          { path: '/equipment/:id/edit', element: <EquipmentFormPage /> },
          { path: '/work-orders', element: <WorkOrdersListPage /> },
          { path: '/work-orders/new', element: <WorkOrderFormPage /> },
          { path: '/work-orders/:id', element: <WorkOrderDetailPage /> },
          { path: '/work-orders/:id/edit', element: <WorkOrderFormPage /> },
          { path: '/maintenance-plans', element: <MaintenancePlansListPage /> },
          { path: '/maintenance-plans/new', element: <MaintenancePlanFormPage /> },
          { path: '/maintenance-plans/:id', element: <MaintenancePlanDetailPage /> },
          { path: '/maintenance-plans/:id/edit', element: <MaintenancePlanFormPage /> },
          { path: '/parts', element: <PartsListPage /> },
          { path: '/parts/new', element: <PartFormPage /> },
          { path: '/parts/:id', element: <PartDetailPage /> },
          { path: '/parts/:id/edit', element: <PartFormPage /> },
          { path: '/stock-lots', element: <StockLotsListPage /> },
          { path: '/stock-lots/new', element: <StockLotFormPage /> },
          { path: '/stock-lots/:id', element: <StockLotDetailPage /> },
          { path: '/stock-lots/:id/edit', element: <StockLotFormPage /> },
          { path: '/certificates', element: <CertificatesListPage /> },
          { path: '/certificates/new', element: <CertificateFormPage /> },
          { path: '/certificates/:id', element: <CertificateDetailPage /> },
          { path: '/drydock-campaigns', element: <DrydockCampaignsListPage /> },
          { path: '/drydock-campaigns/new', element: <DrydockCampaignFormPage /> },
          { path: '/drydock-campaigns/:id', element: <DrydockCampaignDetailPage /> },
          {
            path: '/drydock-campaigns/:id/edit',
            element: <DrydockCampaignFormPage />,
          },
          {
            element: <RoleGuard roles={['isi_admin', 'superadmin']} />,
            children: [
              {
                path: '/admin/taxonomies',
                lazy: lazyComponent(
                  () => import('@/pages/taxonomy/TaxonomyPage'),
                  'TaxonomyPage',
                ),
              },
              {
                path: '/admin/email',
                lazy: lazyComponent(
                  () => import('@/pages/admin-email/AdminEmailPage'),
                  'AdminEmailPage',
                ),
              },
              {
                // L19 — admin cross-vessel monthly reports list.
                path: '/admin/monthly-reports',
                lazy: lazyComponent(
                  () => import('@/pages/monthly-reports/AdminMonthlyReportsPage'),
                  'AdminMonthlyReportsPage',
                ),
              },
              {
                path: '/admin/system',
                lazy: lazyComponent(
                  () => import('@/pages/admin-system/AdminSystemPage'),
                  'AdminSystemPage',
                ),
              },
              {
                path: '/admin/errors',
                lazy: lazyComponent(
                  () => import('@/pages/admin-errors/AdminErrorsPage'),
                  'AdminErrorsPage',
                ),
              },
              {
                path: '/equipment-types',
                lazy: lazyComponent(
                  () => import('@/pages/equipment-types/EquipmentTypesListPage'),
                  'EquipmentTypesListPage',
                ),
              },
              {
                path: '/equipment-types/new',
                lazy: lazyComponent(
                  () => import('@/pages/equipment-types/EquipmentTypeFormPage'),
                  'EquipmentTypeFormPage',
                ),
              },
              {
                path: '/equipment-types/:id',
                lazy: lazyComponent(
                  () => import('@/pages/equipment-types/EquipmentTypeDetailPage'),
                  'EquipmentTypeDetailPage',
                ),
              },
              {
                path: '/equipment-types/:id/edit',
                lazy: lazyComponent(
                  () => import('@/pages/equipment-types/EquipmentTypeFormPage'),
                  'EquipmentTypeFormPage',
                ),
              },
              {
                path: '/part-types',
                lazy: lazyComponent(
                  () => import('@/pages/part-types/PartTypesListPage'),
                  'PartTypesListPage',
                ),
              },
              {
                path: '/part-types/new',
                lazy: lazyComponent(
                  () => import('@/pages/part-types/PartTypeFormPage'),
                  'PartTypeFormPage',
                ),
              },
              {
                path: '/part-types/:id',
                lazy: lazyComponent(
                  () => import('@/pages/part-types/PartTypeDetailPage'),
                  'PartTypeDetailPage',
                ),
              },
              {
                path: '/part-types/:id/edit',
                lazy: lazyComponent(
                  () => import('@/pages/part-types/PartTypeFormPage'),
                  'PartTypeFormPage',
                ),
              },
              {
                path: '/users',
                lazy: lazyComponent(
                  () => import('@/pages/users/UsersListPage'),
                  'UsersListPage',
                ),
              },
              {
                path: '/users/new',
                lazy: lazyComponent(
                  () => import('@/pages/users/UserFormPage'),
                  'UserFormPage',
                ),
              },
              {
                path: '/users/:id',
                lazy: lazyComponent(
                  () => import('@/pages/users/UserDetailPage'),
                  'UserDetailPage',
                ),
              },
              {
                path: '/users/:id/edit',
                lazy: lazyComponent(
                  () => import('@/pages/users/UserFormPage'),
                  'UserFormPage',
                ),
              },
              {
                path: '/companies',
                lazy: lazyComponent(
                  () => import('@/pages/companies/CompaniesListPage'),
                  'CompaniesListPage',
                ),
              },
              {
                path: '/companies/new',
                lazy: lazyComponent(
                  () => import('@/pages/companies/CompanyFormPage'),
                  'CompanyFormPage',
                ),
              },
              {
                path: '/companies/:id',
                lazy: lazyComponent(
                  () => import('@/pages/companies/CompanyDetailPage'),
                  'CompanyDetailPage',
                ),
              },
              {
                path: '/companies/:id/edit',
                lazy: lazyComponent(
                  () => import('@/pages/companies/CompanyFormPage'),
                  'CompanyFormPage',
                ),
              },
              {
                path: '/companies/:id/settings',
                lazy: lazyComponent(
                  () => import('@/pages/companies/CompanySettingsPage'),
                  'CompanySettingsPage',
                ),
              },
            ],
          },
          {
            // Suppliers admin (L3) — fleet_manager joins the admin
            // gate here because they may write suppliers scoped to
            // their own company. Chief/technician keep read-only
            // access via the controller @Roles, and won't see the
            // route in the sidebar (admin gate masks the link).
            element: (
              <RoleGuard
                roles={['isi_admin', 'superadmin', 'fleet_manager']}
              />
            ),
            children: [
              {
                path: '/suppliers',
                lazy: lazyComponent(
                  () => import('@/pages/suppliers/SuppliersListPage'),
                  'SuppliersListPage',
                ),
              },
              {
                path: '/suppliers/:id',
                lazy: lazyComponent(
                  () => import('@/pages/suppliers/SupplierDetailPage'),
                  'SupplierDetailPage',
                ),
              },
              {
                // L8 — Budget report (vessel-level cost aggregates).
                // Same role gate as suppliers: the lot intends fleet
                // managers to read their own company's budget, with
                // admins seeing everything.
                path: '/reports/budget',
                lazy: lazyComponent(
                  () => import('@/pages/budget-report/BudgetReportPage'),
                  'BudgetReportPage',
                ),
              },
              {
                // L14 — Inspection templates (admin / fleet manager
                // can write the catalogue; chiefs/technicians don't
                // see this route in the sidebar but can still execute
                // rounds via the vessel-scoped page).
                path: '/inspection-templates',
                lazy: lazyComponent(
                  () => import('@/pages/inspection-templates/InspectionTemplatesListPage'),
                  'InspectionTemplatesListPage',
                ),
              },
              {
                path: '/inspection-templates/new',
                lazy: lazyComponent(
                  () => import('@/pages/inspection-templates/InspectionTemplateFormPage'),
                  'InspectionTemplateFormPage',
                ),
              },
              {
                path: '/inspection-templates/:id',
                lazy: lazyComponent(
                  () => import('@/pages/inspection-templates/InspectionTemplateFormPage'),
                  'InspectionTemplateFormPage',
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
